/**
 * TokenManager - Centralized token management with automatic refresh.
 * Superset used by both apps; automatically refreshes expired access tokens using refresh tokens.
 */
import { notifySessionExpired } from "./sessionExpiry";
import { AuthErrorKey, mapAuthServerError } from "./mapAuthError";
import type { AuthErrorBody } from "./mapAuthError";

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  id_token?: string;
}

export interface CreateTokenManagerConfig {
  authEndpoint: string;
}

export class TokenManager {
  private refreshPromise: Promise<string> | null = null;
  private readonly authEndpoint: string;

  constructor(authEndpoint: string) {
    this.authEndpoint = authEndpoint;
  }

  /**
   * Get a valid access token, automatically refreshing if expired
   * @returns Promise<string | null> - The access token or null if unavailable
   */
  async getAccessToken(): Promise<string | null> {
    const token = localStorage.getItem("jwt_access_token");
    const expirationTime = localStorage.getItem(
      "jwt_access_token_expirationTime",
    );

    if (!token) {
      return null;
    }

    if (this.isTokenExpired(expirationTime)) {
      try {
        return await this.refreshToken();
      } catch (error) {
        console.error("Token refresh failed:", error);
        this.clearTokens();
        notifySessionExpired();
        return null;
      }
    }

    return token;
  }

  /**
   * Synchronously get access token without refresh check
   * Use this only when you need immediate access and can handle null
   * @returns string | null - The access token or null
   */
  getAccessTokenSync(): string | null {
    return localStorage.getItem("jwt_access_token");
  }

  isTokenExpired(expirationTime: string | null): boolean {
    if (!expirationTime) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const expiration = parseInt(expirationTime, 10);

    if (isNaN(expiration)) {
      return true;
    }

    return expiration <= currentTime + 5;
  }

  isTokenValid(): boolean {
    const token = localStorage.getItem("jwt_access_token");
    const expirationTime = localStorage.getItem(
      "jwt_access_token_expirationTime",
    );
    return !!token && !this.isTokenExpired(expirationTime);
  }

  hasRefreshToken(): boolean {
    return !!localStorage.getItem("jwt_refresh_token");
  }

  async refreshToken(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async refreshTokenWithExternal(
    externalRefreshToken: string,
  ): Promise<string> {
    console.log("Refresh token info:", {
      length: externalRefreshToken.length,
      startsWith: externalRefreshToken.substring(0, 10),
      endsWith: externalRefreshToken.substring(
        externalRefreshToken.length - 10,
      ),
    });

    const tokenParams = new URLSearchParams({
      refresh_token: externalRefreshToken,
      grant_type: "refresh_token",
      scope:
        "offline_access openid profile email phone roles api backup belege",
    });

    const response = await fetch(`${this.authEndpoint}/connect/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: tokenParams.toString(),
    });

    if (!response.ok) {
      let body: AuthErrorBody | undefined;
      try {
        body = (await response.json()) as AuthErrorBody;
      } catch (parseError) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`, {
          cause: parseError,
        });
      }
      console.error("Token refresh error response:", body);
      throw new Error(
        mapAuthServerError(body, AuthErrorKey.TokenRefreshFailed),
      );
    }

    const data: TokenResponse = await response.json();
    this.setTokens(
      data.access_token,
      data.refresh_token,
      data.expires_in,
      data.id_token,
    );

    return data.access_token;
  }

  private async _performRefresh(): Promise<string> {
    const refreshToken = localStorage.getItem("jwt_refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const tokenParams = new URLSearchParams({
      refresh_token: refreshToken,
      grant_type: "refresh_token",
    });

    const response = await fetch(`${this.authEndpoint}/connect/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: tokenParams.toString(),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    const data: TokenResponse = await response.json();
    this.setTokens(
      data.access_token,
      data.refresh_token,
      data.expires_in,
      data.id_token,
    );

    return data.access_token;
  }

  setTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    idToken?: string,
  ): void {
    localStorage.setItem("jwt_access_token", accessToken);
    localStorage.setItem("jwt_refresh_token", refreshToken);
    localStorage.setItem(
      "jwt_access_token_expirationTime",
      String(Math.floor(Date.now() / 1000) + expiresIn),
    );
    localStorage.setItem("jwt_access_token_expirationValue", String(expiresIn));
    if (idToken) {
      localStorage.setItem("jwt_id_token", idToken);
    }
  }

  clearTokens(): void {
    localStorage.removeItem("jwt_access_token");
    localStorage.removeItem("jwt_refresh_token");
    localStorage.removeItem("jwt_id_token");
    localStorage.removeItem("jwt_access_token_expirationTime");
    localStorage.removeItem("jwt_access_token_expirationValue");
  }

  getIdToken(): string | null {
    return localStorage.getItem("jwt_id_token");
  }

  getExpirationTime(): string | null {
    return localStorage.getItem("jwt_access_token_expirationTime");
  }

  getRefreshToken(): string | null {
    return localStorage.getItem("jwt_refresh_token");
  }
}

export function createTokenManager(
  config: CreateTokenManagerConfig,
): TokenManager {
  return new TokenManager(config.authEndpoint);
}
